/**
 *  @file       keystore.js
 *              Wallet API Keystore classes
 *
 *  @author     Wes Garland    - wes@kingsds.network
 *  @author     Duncan Mays    - duncan@kingsds.network
 *  @author     Badrdine Sabhi - badr@kingsds.network
 *  @author     Ryan Rossiter  - ryan@kingsds.network
 *  @author     David Courtis  - david@distributive.network
 *  @date       April 2020
 */
'use strict';

const dcpEnv = require('dcp/common/dcp-env');
const debugging = require('dcp/debugging').scope('wallet/keystore');

const { DCPError,
      }                                      = require('dcp/common/dcp-error');
const { setTimeout,
        clearTimeout,
      }                                      = require('dcp/common/dcp-timers');
const { unlockFailErrorCode,
        malformedObjectErrorCode,
      }                                      = require('dcp/dcp-client/wallet/error-codes');
const { assert,
      }                                      = require('dcp/common/dcp-assert');
const { makeSignedMessageObject,
        makeSignedMessage,
        makeSignedLightWeightMessageObject,
        makeSignature,
      }                                      = require('./make-signature');

/* Place canonical versions of ethereumjs-util and ethereumjs-wallet on
 * exports._internalEth for reference by 'friend' modules.  Try to load
 * native (fast) versions on NodeJS, but fallback to internal modules
 * when not present. Modules are argumented with fast keccak
 * hashing code via wasm for internal use.
 */
exports._internalEth = {};

const dcpEth = require('./eth');
exports._internalEth.impl = require('./make-signature')._internalEth.impl;
exports._internalEth.buffer = require('./make-signature')._internalEth.buffer;
const ethWallet = exports._internalEth.wallet = require('./make-signature')._internalEth.wallet;
const ethUtil = exports._internalEth.util = require('./make-signature')._internalEth.util;

/** Generate a new private key */
function generateNewPrivateKey()
{
  return new dcpEth.PrivateKey(ethWallet.generate().getPrivateKey());
}

/** Accept json from any foreign ethereum wallet [that we understand] and a passphrase,
 *  returning the address and private key
 *
 *  @param      json            {string}        A JSON keystore, eg. ethereum V3 wallet
 *  @param      passphrase      {string}        The passphrase to unlock the keystore
 *  @returns    {Object}                        An object having properties address and privateKey,
 *                                              throws malformedObjectErrorCode if it couldn't be
 *                                              parsed, or false if wrong passphrase.
 */
async function parseForeignKeystore(json, passphrase)
{
  var fks;
  var error = '(no error)';

  try
  {
    fks = await ethWallet.fromV3(json, passphrase, { nonStrict: true });
  }
  catch(e)
  {
    if (e.message === 'Key derivation failed - possibly wrong passphrase')
    {
      debugging() && passphrase && console.debug('warning: Key derivation failed - possibly wrong passphrase', e);
      return false;
    }
    debugging() && passphrase && console.debug('warning: foreign keystore not in wallet-v3 format', e);
  }

  if (!fks) try { fks = await ethWallet.fromV1     (json, passphrase); } catch(e) { error = e; }
  if (!fks) try { fks = await ethWallet.fromEthSale(json, passphrase); } catch(e) { error = e; }

  if (!fks)
  {
    debugging() && console.debug('error: malformed keystore given', error);
    throw new DCPError('malformed keystore', malformedObjectErrorCode);
  }

  return {
    address:    fks.address,
    privateKey: fks.getPrivateKey(),
  };
}

/**
 * Form 1
 *
 * A Keystore object with a randomly generated privateKey and an address
 * that corresponds to it. This form will prompt the user for a password to encrypt itself with.
 * @async
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 2
 *
 * A Keystore object with the provided privateKey and an address that
 * corresponds to it. This form will prompt the user for a password to encrypt itself with.
 * @async
 * @param {string|object} privateKey an Ethereum v3 Keystore object, or privateKey in hex string format
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 3
 *
 * A Keystore object with the provided privateKey and an address that
 * corresponds to it, encrypted with the provided passphrase.
 * @async
 * @param {string|object} privateKey an Ethereum v3 Keystore object, or privateKey in hex string format
 * @param {string} passphrase
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 4
 *
 * A Keystore object parsed from the third party object. If the third party
 * object cannot be parsed the promise will reject.
 * @async
 * @param {object} privateKey an Ethereum v3 Keystore object
 * @param {string} passphrase
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 5
 *
 * A Keystore object parsed from the JSON string. If the JSON or resulting
 * object cannot be parsed the promise will reject.
 * @async
 * @param {string} JsonString - a JSON encoded keystore of some sort
 * @param {string} passphrase
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 6
 *
 * A Keystore object parsed from the third party object. If the third party
 * object cannot be parsed the promise will reject. The user will be prompted for a passphrase.
 * @async
 * @param {object} privateKey Ethereum v3 Keystore object
 * @returns {module:dcp/wallet.Keystore}
 * @access public
 */
/**
 * Form 7
 *
 * A Keystore object with a randomly generated privateKey and an address
 * that corresponds to it. This form will used the supplied passphrase to encrypt the private key.
 * @async
 * @param {null} key unused in this form, when null it will be generated.
 * @param {string|null} passphrase the passphrase used to encrypt the new keystore
 * @returns Promise which resolves to {module:dcp/wallet.Keystore}
 * @access public
 * @example
 * let ks = await new Keystore(null, '');
 */
class Keystore extends require('events').EventEmitter
{
  #passphrase;
  #checkEmpty;
  #ew;                 /* ethereum wallet: (json) backing store for encrypted private keys */
  #unlockBusy;         /* mutex to single-thread unlock */
  #privateKey;         /* The private key when unlocked; falsey when locked */
  #autoUnlockDuration; /* lock duration when doing auto-unlock (like sudo), or falsey */
  #relockTime;         /* when the Keystore will be considered locked again */
  #lockTimerHnd;       /* Timer handle when we have a pending automatic relock on the event queue */
  #debugLabel;

  constructor()
  {
    super();

    for (let prop of [ '_events', '_eventsCount', '_maxListeners' ])
    {
      Object.defineProperty(this, prop, {
        value:        this[prop],
        writable:     true,
        configurable: true,
        enumerable:   false,
      });
    }

    this.#unlockBusy = new (require('dcp/common/concurrency').Synchronizer)('idle', ['idle', 'busy']);
    return this.#a$constructor(...arguments);
  }

  async #a$constructor()
  {
    const { promptCreatePassphrase } = require('../wallet');

    if (arguments.length >= 2)
      this.#passphrase = arguments[1]                              /* all forms which accept passphrases */
    if (arguments.length === 3)
      this.#checkEmpty = arguments[2]                              /* for get form 4, setting checkempty to false */

    if (arguments[0] === null)                               /* form 7 */
      this.#privateKey = generateNewPrivateKey();
    else if (arguments.length === 0)
    {
      this.#privateKey = generateNewPrivateKey();                  /* form 1 */
      this.address = this.#privateKey.toAddress();
      const metaData = {
        label: "New key",
        address: this.address,
      };
      this.#passphrase = await promptCreatePassphrase(metaData);
    }
    else if (dcpEth.PrivateKey(arguments[0], true))              /* form 2, 3 */
    {
      this.#privateKey = new dcpEth.PrivateKey(arguments[0]);
      this.address = this.#privateKey.toAddress();
      if (this.#passphrase === false)
        this.#passphrase = '';
      if (typeof this.#passphrase === 'undefined')
        this.#passphrase = await promptCreatePassphrase({ label: 'New key', address: this.address });
    }
    else if (arguments[0] instanceof exports.Keystore)            /* form 6 */
    {
      this.#privateKey = await arguments[0].getPrivateKey();
      this.#ew = JSON.stringify(arguments[0]);
    }
    else if (typeof arguments[0] === 'string' || typeof arguments[0] === 'object') /* form 4, 5 */
    {
      let jsonO;

      if (typeof arguments[0] === 'object')       /* form 4 */
      {
        this.#ew = JSON.stringify(arguments[0]);
        jsonO = arguments[0]
      }
      else
      {                                    /* form 5 */
        this.#ew = arguments[0];
        try
        {
          jsonO = JSON.parse(String(this.#ew).trim());
        }
        catch (error)
        {
          throw new DCPError('malformed keystore', malformedObjectErrorCode);
        }
      }

      if (jsonO.address)
        this.address = new dcpEth.Address(jsonO.address);
      else
      {
        let fks;

        if (this.#passphrase)
          fks = await parseForeignKeystore(this.#ew, this.#passphrase);
        else
        {
          if (this.#checkEmpty !== false)
            fks = await parseForeignKeystore(this.#ew, '');
          if (!fks)
          {
            /* ks has password */
            let metaData = { label: jsonO.label || jsonO.id };
            const ew = this.#ew;
            fks = await require('../wallet').passphraseTries(metaData,
                                        async function wallet$$Keystore$Constructor$unlock_tryFn(passphrase) {
                                          await parseForeignKeystore(ew, passphrase);
                                        })
          }
        }

        if (!fks)
          throw new DCPError(`Could not unlock foreign keystore ${jsonO.label || jsonO.id} to determine public address`, unlockFailErrorCode);
        this.address = fks.address;
      }
      if (jsonO.label)
        this.label = jsonO.label;
    }

    /* By the bottom of this funnel, we have the
     * - defined this.address
     * - dcpEth.PrivateKey or a ethereum wallet (JSON string)
     * - passphrase or '' if making a Keystore from a dcpEth.PrivateKey
     *
     * Now we make sure that we can find the private key by unlocking ew,
     * have an address, and forget the private key
     */
    if (!this.#ew)
    {
      let buf = ethUtil.toBuffer(this.#privateKey);
      let i = ethWallet.fromPrivateKey(buf);
      this.#ew = await i.toV3String(this.#passphrase || '', { n: 1024 });
    }
    if (this.#privateKey && !this.address)
      this.address = this.#privateKey.toAddress();
    this.#privateKey = null;
    this.#passphrase = null;
    this.#debugLabel = this.label || this.address;
    this.#resetLockState();

    return this;
  }

  /**
   * This method unlocks the Keystore object for either a single use or a given amount of time. When the
   * object is unlocked, `getPrivateKey` and related operations do not need to ask for a passphrase, as
   * the private key is "remembered" internally.  If time is not specified, the object will be immediately
   * locked after the next call to `getPrivateKey`; otherwise, it will remain unlocked until the timer runs out.
   *
   * When the unlock timer is running, calling `unlock` can have multiple effects. In all cases the current
   * running timer will be increased to lockTimerDuration if that will increase the time left.
   *   1. If the autoReset flag is true for this invocation, the autoReset duration is updated only if it
   *      will be increased. Thus autoReset is a sticky setting, it can be turned on but not off (except by locking)
   *   2. If the autoReset flag is false for this invocation, the current unlock duration is increased, but
   *      the autoReset duration remains the same.
   * @method module:dcp/wallet.Keystore#unlock
   * @param  {string} [passphrase] Passphrase to unlock keystore. If null|undefined, `passphrasePrompt`
   *                               function is invoked to solicit it from the user if the keystore
   *                               is not already unlocked.
   * @param  {number} lockTimerDuration Number of seconds to keep keystore unlocked for. Intended to implement features like `sudo`
   * @param  {boolean} [autoReset] Flag to determine if unlock timer should reset on subsequent calls.
   * @access public
   * @async
   * @throws Error with code 'DCP_WA:UNLOCKX' when keystore could not be unlocked.
   */
  async unlock(passphrase, lockTimerDuration, autoReset)
  {
    const mx = this.#unlockBusy;
    do /* spin lock to single thread this per keystore */
    {
      mx.setMaxListeners(mx.getMaxListeners() + 1);
      await mx.until('idle');
      mx.setMaxListeners(mx.getMaxListeners() - 1);
    } while(!mx.testAndSet('idle', 'busy'));

    try
    {
      this.#lockIfTimerExpired();
      if (!this.#privateKey)
      {
        await this.#extractAndSetPrivateKey(passphrase);
        if (!this.#privateKey)
          throw new DCPError(`Could not unlock keystore '${this.#debugLabel}'`, unlockFailErrorCode);
      }
      assert(this.#privateKey);
      this.emit('unlock');

      /* If in autoReset mode, increase the autoUnlockDuration if a longer one was specified */
      if (autoReset && lockTimerDuration > this.#autoUnlockDuration)
        this.#autoUnlockDuration = lockTimerDuration;

      /* Make this unlock expire in lockTimerDuration seconds [or later if there was a previous longer one] */
      if (lockTimerDuration >= 0)
        this.scheduleRelock(lockTimerDuration);
      debugging('locks') && console.log(`wallet - ${this.#debugLabel} is unlocked`);
    }
    finally
    {
      this.#unlockBusy.set('busy', 'idle');
    }

    return this.#privateKey;
  }

  /**
   * Lock the Keystore; make private key no longer accessible without password entry.
   * @method module:dcp/wallet.Keystore#lock
   * @access public
   */
  lock()
  {
    let debugMsg = `wallet - lock ${this.#debugLabel}`;
    if (this.isUnlocked())
      debugMsg += ' (was not locked)';
    debugging('locks') && console.log(debugMsg);

    this.#resetLockState();
    this.emit('lock');
  }

  /**
   * Returns private key, may prompt for password. If you need this you're probably doing something wrong.
   * @method module:dcp/wallet.Keystore#getPrivateKey
   * @returns {module:dcp/wallet.PrivateKey}
   * @async
   * @access public
   */
  async getPrivateKey()
  {
    let pk;

    this.#lockIfTimerExpired();
    if (this.isLocked())
    {
      pk = await this.unlock();
      this.lock();
    }
    else
    {
      pk = this.#privateKey;
      if (this.#autoUnlockDuration > 0)
      {
        debugging('timer') && console.log(`wallet - auto reset new lock expire time on ${this.#debugLabel} - ${this.#autoUnlockDuration * 1000}ms`);
        this.scheduleRelock(this.#autoUnlockDuration);
      }
      else if (!this.#lockTimerHnd)
      {
        this.lock();
      }
    }

    debugging('getPrivateKey') && console.log(`wallet - ${this.#debugLabel} pk=${pk}`);
    return pk;
  }

  /**
   * Schedule the next automatic lock of this keystore, overriding any earlier-firing re-lock
   * @param {number} newDuration     seconds until the keystore gets re-locked
   */
  scheduleRelock(newDuration)
  {
    var newDurationMs = newDuration * 1000;
    var next_relockTime = Date.now() + newDurationMs;

    if (next_relockTime < this.#relockTime)
      return;

    debugging('timer') && console.log(`wallet - setting lock expire time on ${this.#debugLabel} - ${next_relockTime}s`);
    this.#relockTime = next_relockTime;
    if (this.#lockTimerHnd)
      clearTimeout(this.#lockTimerHnd);
    this.#lockTimerHnd = setTimeout(this.lock.bind(this), newDurationMs);
    this.#lockTimerHnd.unref();
  }

  /**
   * Is the keystore presently unlocked?
   * @method module:dcp/wallet.Keystore#isUnlocked
   * @returns {boolean}
   * @access public
   */
  isUnlocked()
  {
    return !!this.#privateKey;
  }

  /**
   * Is the keystore presently locked?
   * @method module:dcp/wallet.Keystore#isLocked
   * @returns {boolean}
   * @access public
   */
  isLocked()
  {
    return !this.isUnlocked();
  }

  /**
   * Test a passphrase to see if it unlocks the private key in this Keystore
   * @method module:dcp/wallet.Keystore#testPassphrase
   * @param {string} passphrase      the passphrase to test
   * @returns {Promise} Promise that resolve true if the passphrase unlocks the private key in this Keystore; otherwise false.
   * @access public
   */
  async testPassphrase(passphrase)
  {
    var result = false;
    try
    {
      result = Boolean(await parseForeignKeystore(this.#ew, passphrase));
    }
    catch(error){};

    return result;
  }

  /**
   * Generate JSON object literal representation of this keystore. This can later be passed to
   * the constructor to generate a new Keystore object.
   *
   * @access public
   * @returns {object}
   * @function module:dcp/wallet.Keystore#toJSON
   */
  toJSON()
  {
    let base = JSON.parse(this.#ew);

    if (this.label)
      base.label = this.label;

    base.timestamp = String(new Date());

    if (!dcpEnv.isBrowserPlatform)
      base.process = dcpEnv.getProcessName();
    else
    {
      const url = document.currentScript ? new URL(document.currentScript.src) : window.location
      base.process = url.origin + url.pathname;
    }

    return base;
  }

  #resetLockState()
  {
    debugging('timer') && console.debug(`wallet - reset lock timer state on ${this.#debugLabel}`);

    if (this.#lockTimerHnd)
      clearTimeout(this.#lockTimerHnd)

    this.#privateKey         = undefined;
    this.#autoUnlockDuration = 0;
    this.#relockTime         = 0;
    this.#lockTimerHnd       = undefined;
  }

  /**
   * Check the timer state, re-locking the Keystore as needed. It is
   * important to call this at the start of timer-sensitive operations, as
   * we can't rely on setTimeout to re-lock Keystores; we must consider the
   * possibility that starving the event loop could cause the private key
   * to be stored for longer than intended
   */
  #lockIfTimerExpired()
  {
    debugging('timer') && console.log(`wallet - lockIfTimerExpired on ${this.#debugLabel}`);
    if (this.#lockTimerHnd && this.#relockTime <= Date.now())
      this.lock();
  }

  /** used internally by unlock */
  async #extractAndSetPrivateKey(unlock_passphrase)
  {
    var fks;

    /* used by passphraseTries */
    const unlock_tryFn = (passphrase) => {
      if (this.#privateKey) /* unlocked by other "thread" - ideally would never happen... */
        return { address: this.address, privateKey: this.#privateKey };
      return parseForeignKeystore(this.#ew, passphrase);
    }

    /* used by passphraseTries */
    const unlock_skipCheckFn = () => Boolean(this.#privateKey);

    if (unlock_passphrase !== undefined && unlock_passphrase !== null)
      fks = await parseForeignKeystore(this.#ew, unlock_passphrase);
    else
    {
      if (this.#checkEmpty !== false)
        fks = await parseForeignKeystore(this.#ew, '');
      if (!fks)
        fks = await require('../wallet').passphraseTries({ label: this.label , address: this.address }, unlock_tryFn, unlock_skipCheckFn);
    }

    if (fks)
      this.#privateKey = new dcpEth.PrivateKey(fks.privateKey);
  }
}

exports.Keystore = Keystore;

/**
 * Creates and returns stringified signedMessage in the format:
 *       JSON.stringify({ owner: this.address, signature: signature, body: messageBody })
 * @async
 * @access public
 * @param {object} messageBody
 * @returns {Promise<string>}
 * @function module:dcp/wallet.Keystore#makeSignedMessage
 */
exports.Keystore.prototype.makeSignedMessage = async function wallet$$Keystore$makeSignedMessage(messageBody) {
  const privateKey = await this.getPrivateKey();
  return makeSignedMessage(messageBody, privateKey);
}

/** @typedef {object} SignedMessageObject
 * @property {object} body
 * @property {Signature} signature
 * @property {module:dcp/wallet.Address} owner
 */

/**
 * Creates and returns stringification of SignedMessageObject in the format:
 *       { owner: this.address, signature: signature, body: messageBody }
 * @async
 * @access public
 * @param {object} messageBody
 * @returns {Promise<SignedMessageObject>}
 * @function module:dcp/wallet.Keystore#makeSignedMessageObject
 */
exports.Keystore.prototype.makeSignedMessageObject = async function wallet$$Keystore$makeSignedMessageObject(messageBody) {
  const privateKey = await this.getPrivateKey();
  return makeSignedMessageObject(messageBody, privateKey, this.address);
}

/** @typedef {object} SignedLightWeightMessageObject
 * @property {object} body
 * @property {object} auth
 * @property {Signature} signature
 * @property {module:dcp/wallet.Address} owner
 */

/**
 * Creates and returns SignedLightWeightMessageObject in the format:
 *       { owner: this.address, signature: signature, auth: messageLightWeight, body: messageBody };
 * The signature is computed only on messageLightWeight, in order to improve perf when messageBody is large.
 * @async
 * @access public
 * @param {object} messageLightWeight
 * @param {object} messageBody
 * @returns {Promise<SignedLightWeightMessageObject>}
 * @function module:dcp/wallet.Keystore#makeSignedLightWeightMessageObject
 */
exports.Keystore.prototype.makeSignedLightWeightMessageObject = async function wallet$$Keystore$makeSignedLightWeightMessageObject(messageLightWeight, messageBody) {
  const privateKey = await this.getPrivateKey();
  return makeSignedLightWeightMessageObject(messageLightWeight, messageBody, privateKey, this.address)
}

/** @typedef {object} Signature
 * @property {Uint8Array} r
 * @property {Uint8Array} s
 * @property {Uint8Array} v
 */

/**
 * Creates and returns signature for messageBody
 *       { owner: this.address, signature: signature, auth: messageLightWeight, body: messageBody };
 * The signature is computed only on messageLightWeight, in order to improve perf when messageBody is large.
 * @async
 * @access public
 * @param {object} messageBody
 * @returns {Promise<Signature>}
 * @function module:dcp/wallet.Keystore#makeSignature
 */
exports.Keystore.prototype.makeSignature = async function wallet$$Keystore$makeSignature(messageBody)
{
  if (typeof messageBody !== 'string') {
    if (typeof messageBody === 'undefined')
      throw new Error('tried to make a signature for undefined');
    messageBody = JSON.stringify(messageBody);
  }

  const privateKey = await this.getPrivateKey();
  return makeSignature(messageBody, privateKey);
}

/**
 * IdKeystore is a class for holding keystores that describe identities.
 *
 * See {@link module:dcp/wallet.Keystore|Keystore constructor} for documentation on all the ways to call this constructor.
 * @classdesc Subclass of {@link module:dcp/wallet.Keystore|Keystore}
 * @class IdKeystore
 * @async
 * @extends module:dcp/wallet.Keystore
 * @access public
 */
exports.IdKeystore = class IdKeystore extends Keystore
{
  constructor()
  {
    return super(...arguments);
  }
}

/**
 * AuthKeystore is s class for holding keystores that describe authorizations to do things.
 *
 * See {@link module:dcp/wallet.Keystore|Keystore constructor} for documentation on all the ways to call this constructor.
 * @classdesc Subclass of {@link module:dcp/wallet.Keystore|Keystore}
 * @class AuthKeystore
 * @async
 * @extends module:dcp/wallet.Keystore
 * @access public
 */
exports.AuthKeystore = class AuthKeystore extends Keystore
{
  constructor()
  {
    return super(...arguments);
  }
}

/**
 * BankAccountKeystore is a class for holding keystores that describe bank accounts.
 * Knowing the private key implicitly grants authorization to do everything related to
 * the bank account.
 *
 * It inherits from Keystore.
 */
exports.BankAccountKeystore = class BankAccountKeystore extends Keystore
{
  constructor()
  {
    return super(...arguments);
  }
}

/**
 * Alert user that a key from storage will be used, give them a chance to ditch it.
 *
 * @param {string} storageKey key in local or session storage where the file is stored
 * @param {string} jobName helps user know which key is retrieved from storage
 *
 * @returns {Promise<string | boolean>} contents of Keystore file
 */
exports.getKeystoreFromStorage = async function getKeystoreFromStorage (storageKey, jobName)
{
  const ClientModal = require('dcp/dcp-client/dom-tk').modals;

  let keystoreFile = sessionStorage.getItem(storageKey);
  if (keystoreFile) return keystoreFile;

  keystoreFile = localStorage.getItem(storageKey);
  if (keystoreFile)
  {
    let ks = JSON.parse(keystoreFile);
    let label = ks.label || ks.address;
    let prompt;
    // alert is not necessarily for a job, specify jobname only if there is an actual job
    if(jobName)
      prompt = `You have keystore "${label}" saved for ${jobName}. Do you want to use it?`;
    else
      prompt = `You have keystore "${label}". Do you want to use it?`;
    let opts = { buttons: ['Yes', 'No'], title: 'Keystore File' };
    let confirmation = await ClientModal.confirm(prompt, opts);
    if (confirmation)
    {
      sessionStorage.setItem(storageKey, keystoreFile)
      return keystoreFile;
    }
    localStorage.removeItem(storageKey);
    return false;
  }
}

if (dcpEnv.platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const inspect = requireNative('util').inspect.custom;
  exports.Keystore.prototype[inspect]            = function() { return `[Keystore ${           this.isLocked() ? 'l' : 'u'} ${this.address} (${this.label})]` };
  exports.IdKeystore.prototype[inspect]          = function() { return `[IdKeystore ${         this.isLocked() ? 'l' : 'u'} ${this.address} (${this.label})]` };
  exports.AuthKeystore.prototype[inspect]        = function() { return `[AuthKeystore ${       this.isLocked() ? 'l' : 'u'} ${this.address} (${this.label})]` };
  exports.BankAccountKeystore.prototype[inspect] = function() { return `[BankAccountKeystore ${this.isLocked() ? 'l' : 'u'} ${this.address} (${this.label})]` };
}
